/**
 * MapPage.js
 * ---------------------------------------------------------------------
 * Top-level map view that orchestrates:
 *  - Loading GeoJSON for constituencies and wards
 *  - Fetching primary/secondary metrics (Apache Arrow IPC)
 *  - Styling features based on current group/mode
 *  - Handling clicks, hover, drill-down and selection
 *  - Surfacing a concise, aggregated summary to the sidebar
 *
 * Responsibilities here are intentionally kept to orchestration and UI events.
 * Heavy logic is delegated to utils in ./utils/mapPage/* for clarity and testability.
 */
import React, { Component } from "react";
import { Map as ReactMap, Source, Layer } from "react-map-gl";
import SelectionContext from "./state/SelectionContext";
import { withRouter } from "./withRouter";
import MapToolbar from "./MapToolbar";
import MapHoverPopup from "./MapHoverPopup";
import { fetchAudienceInsight } from "./utils/mapPage/fetchAudienceInsight";
import { fetchConstituenciesBaseline } from "./utils/mapPage/fetchConstituenciesBaseline";
import { fetchMetricsUnfiltered as fetchMetricsUnfilteredUtil } from "./utils/mapPage/fetchMetricsUnfiltered";
import { getAggregatesForSidebar as aggregatesForSidebar } from "./utils/mapPage/getAggregatesForSidebar";
import { getFeatureBounds, fitToGeo as fitToGeoUtil, fitBoundsFromFeatures as fitBoundsUtil } from "./utils/mapPage/mapBounds";
import { tableFromIPC } from "apache-arrow";
import { restyleGeo } from "./utils/mapPage/restyleGeo";
import { fetchMetrics } from "./utils/mapPage/fetchMetrics";
import { handleGeoLevelChange } from "./utils/mapPage/handleGeoLevelChange";
import { fetchSecondaryMetrics } from "./utils/mapPage/fetchSecondaryMetrics";
import { handleMapClick } from "./utils/mapPage/onMapClick";
import { loadGeo } from "./utils/mapPage/loadGeo";
import {
  buildLabelsAndColors,
  rowsFromTable,
  makeHeatScale,
  make2pointHeatScale,
  normalizeId,
  parsePCON24CDs,
} from "./utils/mapPage/helpers";
import { DEMO_LABELS } from "./utils/demographicLabels";
import {
  LEVEL_CONSTITUENCY,
  LEVEL_LOCAL_AUTHORITY,
  LEVEL_WARD,
  hasLocalAuthorityLevel,
  isTopLevel,
  levelIdKey,
  levelLabel,
  levelNameKey,
} from "./utils/geoLevels";
import { precomputeCentroids, buildCompareArrows, registerArrowImage } from "./buildCompareArrows";
const MAPBOX_TOKEN =
  "pk.eyJ1IjoibXppZW1pYW4iLCJhIjoiY2xhOXdpc2h5MDI1MzNxbG1wdzcxOWNlZSJ9.Km0c9-jUUlcBs6ByXYbYZA";

// UK mainland bounds used for reset/initial views
const MAINLAND_BOUNDS = [
  [-7.8, 49.9], // SW
  [ 1.6, 58.8], // NE
];
const MAP_STYLE = "mapbox://styles/mapbox/light-v11";

const OVERLAP_MODE = "product";


class MapPage extends Component {
  static contextType = SelectionContext;

  constructor(props) {
    super(props);
    this.state = {
      geoLevel: "constituency",
      // The area drilled into, and which top level it came from. Named for the
      // constituency it used to always be; a local authority is equally valid,
      // and selectedParentLevel says which so callers can fetch and label at
      // the right level.
      selectedConstituencyCode: null,
      selectedParentLevel: null,
      geoData: null,
      styledGeo: null,
      metricRows: null,
      metricRowsUnfiltered: null,
      metricRowsConstituencyUnfiltered: null,
      SecondarymetricRows: null,
      SecondarymetricRowstoplevel: null,
      metricRowsConstituencies: null,
      baselineLoading: false,
      loading: false,
      hoverInfo: null,
      hoverAreaId: null,
      styleLoaded: false,
      selectedFeature: null,
      ranked: [],
      extData: null,
      isMetricsLoading: false,
  extBlurbs: null,
      centroids: null,
      compareArrows: null,
      compareRowsLatest: null,
      compareMetricRows: null,
      newThisWaveKeys: null,
      compareWavePair: null,
      geoLoading: { constituency: false, ward: false },
    };

    this.mapRef = React.createRef();
    this._inflight = null;
    this._lastKey = null;
    this._lastAt = 0;
    this._unfilteredTracking = { inflight: null, lastKey: null, lastAt: 0 };
    this._constituencyUnfilteredTracking = { inflight: null, lastKey: null, lastAt: 0 };
    this._constituencyFilteredInternals = { inflight: null, lastKey: null, lastAt: 0 };
  }

  componentDidMount() {
    // Initial geometry + metrics for the nationwide constituency view
    this.loadGeo("constituency", null, { doFit: true, bounds: MAINLAND_BOUNDS });
    this.fetchMetrics("constituency", null);
    this.fetchConstituenciesBaseline();
    window.addEventListener("resize", this.fitUK);

    // Preload the ward geometry so the first drill-in is instant — but only
    // once the browser is idle. Firing it here alongside the constituency
    // fetch made the two files compete for the same connection pool and
    // delayed the first paint of the map by seconds on a cold load.
    this.scheduleWardPreload();
  }

  scheduleWardPreload = () => {
    const run = () => {
      this._wardPreloadHandle = null;
      if (this._unmounted) return;
      this.loadGeo(LEVEL_WARD, null, { backgroundOnly: true, updateAreas: false, doFit: false });
    };

    if (typeof window.requestIdleCallback === "function") {
      // The timeout is the backstop: on a permanently busy tab the idle
      // callback might otherwise never fire.
      this._wardPreloadHandle = window.requestIdleCallback(run, { timeout: 4000 });
      this._cancelWardPreload = () =>
        window.cancelIdleCallback?.(this._wardPreloadHandle);
    } else {
      this._wardPreloadHandle = setTimeout(run, 2000);
      this._cancelWardPreload = () => clearTimeout(this._wardPreloadHandle);
    }
  };

  fitUK = () => {
    const map = this.mapRef.current?.getMap?.();
    if (!map) return;

    map.fitBounds(MAINLAND_BOUNDS, {
      padding: { top: 24, bottom: 14, left: 24, right: 24 },
      duration: 600,
    });
  };

  flyTo = (options) => {
    const map = this.mapRef.current?.getMap?.();
    if (!map) return;
    if (options.type === "fitBounds") {
      map.fitBounds(options.bounds, { padding: options.padding ?? 24, duration: options.duration ?? 1400 });
    } else {
      map.flyTo({ center: options.center, zoom: options.zoom, duration: options.duration ?? 1400, essential: true });
    }
  };
  componentWillUnmount() {
    this._unmounted = true;
    window.removeEventListener("resize", this.fitUK);
    if (this._wardPreloadHandle != null) this._cancelWardPreload?.();
  }
  
  componentDidUpdate(prevProps, prevState) {
    if (prevState.loading !== this.state.loading) {
      this.props.onLoadingChange?.(this.state.loading);
    }
    if (prevState.baselineLoading !== this.state.baselineLoading) {
      this.props.onBaselineLoadingChange?.(this.state.baselineLoading);
    }
    if (prevProps.hoverArea !== this.props.hoverArea && !this.props.hoverArea) {
      if (this.state.hoverInfo || this.state.hoverAreaId) {
        this.setState({ hoverInfo: null, hoverAreaId: null });
      }
    }
        const prevFilters =
      (typeof prevProps.filters === "object" && prevProps.filters) || {
        gender: prevProps.genderFilter ?? null,
        age: prevProps.ageFilter ?? null,
        ethnicity: prevProps.ethnicityFilter ?? null,
        party: prevProps.partyFilter ?? null,
        education: prevProps.educationFilter ?? null,
        segment: prevProps.segmentFilter ?? null,
      };
    const curFilters = this.getFilters();
    const variableChanged = prevProps.selectedVariable !== this.props.selectedVariable;
    const filtersChanged = JSON.stringify(prevFilters) !== JSON.stringify(curFilters);
    const groupChanged = prevProps.selectedGroupId !== this.props.selectedGroupId;
    const sectionChanged = prevProps.selectedSection !== this.props.selectedSection;
    const modeChanged = prevProps.ActionMode !== this.props.ActionMode;
    const levelChanged = prevState.geoLevel !== this.state.geoLevel;
    const constituencyChanged =
      prevState.selectedConstituencyCode !== this.state.selectedConstituencyCode;
    const targetVarsChanged =
      prevProps.ActionTargetVariables?.TargetVariable1 !==
        this.props.ActionTargetVariables?.TargetVariable1 ||
      prevProps.ActionTargetVariables?.TargetVariable2 !==
        this.props.ActionTargetVariables?.TargetVariable2;
    const ActionTargetSubVariablesChanged =
      prevProps.ActionTargetSubVariables !== this.props.ActionTargetSubVariables;
    const ActionTargetRangesChanged =
      prevProps.ActionTargetRanges !== this.props.ActionTargetRanges;
    const ActionTargetConcentrationChanged =
      prevProps.ActionTargetConcentration !== this.props.ActionTargetConcentration;
    const waveSelect =
      prevProps.waveSelect !== this.props.waveSelect;
  
    // Local authority is only offered for schemas that stratify to it. Switching
    // to a question from another schema removes the pill, so drop back to
    // constituency rather than leaving the map on a level whose data requests
    // now come back empty. Drilled into a district's wards counts too: the ward
    // rows still resolve, but the district tier they are compared against no
    // longer exists.
    const onLocalAuthority =
      this.state.geoLevel === LEVEL_LOCAL_AUTHORITY ||
      (this.state.geoLevel === LEVEL_WARD &&
        this.state.selectedParentLevel === LEVEL_LOCAL_AUTHORITY);
    if (
      (groupChanged || sectionChanged || modeChanged) &&
      onLocalAuthority &&
      !hasLocalAuthorityLevel(this.getActiveGroup()?.schema)
    ) {
      // No early return: the rest of this cycle still has to refetch the
      // baseline for the new question, and the level change queues its own
      // fetch on top.
      this.handleGeoLevelChange(LEVEL_CONSTITUENCY);
    }

    if (levelChanged || constituencyChanged) {
      this.loadGeo(this.state.geoLevel, this.state.selectedConstituencyCode, { doFit: false });
    }
  
    const shouldRefetchMain =
      filtersChanged ||
      variableChanged ||
      groupChanged ||
      sectionChanged ||
      modeChanged ||
      levelChanged ||
      constituencyChanged ||
      targetVarsChanged;
  
    if (shouldRefetchMain) {
      this.fetchMetrics(this.state.geoLevel, this.state.selectedConstituencyCode);
      // At ward level, also fetch constituency-level filtered data in parallel so
      // the constituency comparison bar updates fast (single area, not all 650).
      if (this.state.geoLevel === "ward" && this.state.selectedConstituencyCode) {
        this.fetchConstituencyFiltered(this.state.selectedConstituencyCode);
      }
    }
  
    const shouldRefetchBaseline =
      filtersChanged ||
      groupChanged ||
      sectionChanged ||
      modeChanged ||
      variableChanged;
  
    if (shouldRefetchBaseline) {
      this.fetchConstituenciesBaseline();
    }
  
    const mapDataChanged =
      prevState.metricRows !== this.state.metricRows ||
      prevState.geoData !== this.state.geoData ||
      groupChanged ||
      modeChanged ||
      this.props.preferenceFilter !== prevProps.preferenceFilter ||
      targetVarsChanged ||
      ActionTargetSubVariablesChanged ||
      ActionTargetRangesChanged ||
      ActionTargetConcentrationChanged||
      waveSelect;

    if (mapDataChanged) {
      this.restyle();
    }

    if (prevState.geoData !== this.state.geoData && this.state.geoData?.features) {
      const centroids = precomputeCentroids(this.state.geoData.features);
      this.setState({ centroids });
    }

    const compareChanged  = prevProps.compareWaves !== this.props.compareWaves;
    const waveChanged     = prevProps.waveSelect !== this.props.waveSelect;
    const arrowsRelevant  = compareChanged || waveChanged || mapDataChanged ||
                            prevState.centroids !== this.state.centroids ||
                            prevProps.selectedGroupId !== this.props.selectedGroupId ||
                            prevProps.compareKey !== this.props.compareKey;
    if (arrowsRelevant) {
      this.buildArrows();
    }
    // Switching compare-waves off in target mode should immediately restore
    // the standard target ranking in the sidebar list.
    if (compareChanged && !this.props.compareWaves && this.props.ActionMode === "filter") {
      this.restyle();
    }
  
    const sidebarDataChanged =
      prevState.metricRows !== this.state.metricRows ||
      prevState.metricRowsConstituency !== this.state.metricRowsConstituency ||
      prevState.metricRowsConstituencies !== this.state.metricRowsConstituencies ||
      prevState.metricRowsUnfiltered !== this.state.metricRowsUnfiltered ||
      prevState.metricRowsConstituencyUnfiltered !== this.state.metricRowsConstituencyUnfiltered ||
      // Note: filtersChanged and variableChanged are intentionally excluded — those
      // trigger a refetch, and the sidebar will update when the new metricRows land.
      // Including them here causes the sidebar to render with mismatched labels/data.
      groupChanged ||
      sectionChanged ||
      levelChanged ||
      constituencyChanged ||
      waveChanged ||
      compareChanged ||
      this.props.preferenceFilter !== prevProps.preferenceFilter;
  
    if (sidebarDataChanged) {
      const data = this.getAggregatesForSidebar();

      this.props.onFeatureSelect?.({
       feature: this.state.selectedFeature,
        data,
        constCode: this.state.selectedConstituencyCode,
        constName: this.state.selectedConstituencyName || null,
      });
    }
  
    if (
      this.props.selectedAreaIdToZoom &&
      this.props.selectedAreaIdToZoom !== prevProps.selectedAreaIdToZoom
    ) {
      this.zoomToAreaId(this.props.selectedAreaIdToZoom);
    }

    if (prevProps.audienceInsightRequestId !== this.props.audienceInsightRequestId) {
      this.fetchTopDemographicInsight();
    }

    const chatHighlightChanged = prevProps.chatHighlightAreas !== this.props.chatHighlightAreas;
    if (chatHighlightChanged) {
      this._applyChatFeatureState(prevProps.chatHighlightAreas, this.props.chatHighlightAreas);
      if (this.props.chatHighlightAreas?.length > 0 && this.props.chatShouldZoom) {
        this.zoomToChatAreas(this.props.chatHighlightAreas);
      }
    }

    const mapReadyForChatHighlights =
      this.state.styleLoaded &&
      this.state.geoData?.features &&
      (prevState.styleLoaded !== this.state.styleLoaded || prevState.geoData !== this.state.geoData);

    if (mapReadyForChatHighlights && this.props.chatHighlightAreas?.length > 0) {
      this._applyChatFeatureState([], this.props.chatHighlightAreas);
    }

    if (targetVarsChanged || ActionTargetSubVariablesChanged || levelChanged || constituencyChanged || sectionChanged) {
      this.props.onAudienceInsightChange?.(null);
      this.props.onAudienceInsightLoadingChange?.(false);
    }

    if (prevState.selectedConstituencyCode !== this.state.selectedConstituencyCode) {
      this.context.setSelectedConstituencyCode(this.state.selectedConstituencyCode);
    }
    if (prevState.selectedConstituencyName !== this.state.selectedConstituencyName) {
      this.context.setSelectedConstituencyName(this.state.selectedConstituencyName);
    }
    if (prevState.geoLevel !== this.state.geoLevel) {
      this.context.setGeoLevel(this.state.geoLevel);
    }
  }
  

  _baselineTracking = { inflight: null, lastKey: null, lastAt: 0 };


  // Normalise presentation/selection IDs (minimal formatting)
  normalizeAreaIdForLevel = (level, id) => String(id ?? "");

  // Placeholder for compare-wave support (safe fallback)
  getLatestTwoWaves = () => null;

  /**
   * Returns active demographic filters from props in a normalised object.
   */
  getFilters = () => {
    if (this.props.filters && typeof this.props.filters === "object") {
      const hasAny = Object.values(this.props.filters).some((v) => v != null && v !== "");
      if (hasAny) return this.props.filters;
    }
    const { genderFilter = null, ageFilter = null, ethnicityFilter = null, partyFilter = null, educationFilter = null, segmentFilter = null } = this.props;
    return { gender: genderFilter, age: ageFilter, ethnicity: ethnicityFilter, party: partyFilter, education: educationFilter, segment: segmentFilter };
  };

  _audienceInsightTracking = { key: null, abort: null };
  fetchTopDemographicInsight = async () => {
    // Clear dedup key so re-triggering always runs a fresh fetch.
    this._audienceInsightTracking.key = null;
    await fetchAudienceInsight({
      props:          this.props,
      state:          this.state,
      getActiveGroup: this.getActiveGroup,
      _tracking:      this._audienceInsightTracking,
    });
  };

  // Group helpers
  getGroups = () => this.props.groupsById || {};
  getActiveGroup = () => {
    const groupsById = this.getGroups();
    if (this.props.ActionMode === "explore") {
      return groupsById?.[this.props.selectedGroupId] || null;
    }
    if (this.props.ActionMode === "filter") {
      const t = this.props.ActionTargetVariables || {};
      const tv1 = groupsById?.[t.TargetVariable1] || null;
      const tv2 = groupsById?.[t.TargetVariable2] || null;
      return {
        TargetVariable1: tv1,
        TargetVariable2: tv2,
        // Keep a top-level schema for callers that expect group.schema.
        schema: tv1?.schema || tv2?.schema || null,
      };
    }
    return null;
  };

  /**
   * For likert_grid variables, returns the resolved sub-group when a sector is selected,
   * or the parent grid group for the compare-all view.
   * MapHoverPopup uses this so it can show full likert details for a selected sector.
   */
  getEffectiveGroup = () => {
    const group = this.getActiveGroup();
    if (group?.type !== "likert_grid") return group;
    const pref = this.props.preferenceFilter;
    if (!pref || pref === "compare") return group;
    const [subVarId] = pref.split("|");
    return this.getGroups()[subVarId] || group;
  };

  /**
   * When a sector is selected within a likert_grid variable, returns the effective
   * preference filter for the sub-group (e.g. "net" or a specific response key).
   */
  getEffectivePreferenceFilter = () => {
    const group = this.getActiveGroup();
    if (group?.type !== "likert_grid") return this.props.preferenceFilter;
    const pref = this.props.preferenceFilter;
    if (!pref || pref === "compare") return "compare";
    const parts = pref.split("|");
    return parts.length > 1 ? parts[1] : "net";
  };

  // Builds a unique key to throttle duplicate fetches
  requestKey = (level, areaCode) => {
    const group = this.getActiveGroup();
    const theme = this.props.selectedSection || "explore";
    const filters = this.getFilters();
    let groupKeys = [];
    if (this.props.ActionMode === "explore") groupKeys = group?.keys || [];
    else if (this.props.ActionMode === "filter") {
      const t1 = group?.TargetVariable1;
      const t2 = group?.TargetVariable2;
      const selectedSubVar = this.props.ActionTargetSubVariables?.TargetSubVariable1;
      if (t1?.type === "likert_grid") {
        const [subVarId] = String(selectedSubVar || "").split("|");
        groupKeys = [
          ...(this.getGroups()?.[subVarId]?.keys || []),
          ...(t2?.keys || []),
        ];
      } else {
        groupKeys = [ ...(t1?.keys || []), ...(t2?.keys || []) ];
      }
    }
    const sortedFilters = Object.keys(filters).sort().reduce((o,k)=> (o[k]=filters[k], o), {});
    return [
      level,
      areaCode || "*",
      theme,
      groupKeys.join(","),
      this.props.ActionTargetSubVariables?.TargetSubVariable1 || "",
      JSON.stringify(sortedFilters),
      this.props.ActionMode || "explore",
    ].join("|");
  };


  

  /**
   * Fits the map to a specific area by code and updates selectedFeature.
   */
  zoomToAreaId = (areaId) => {
    const fc = this.state.styledGeo || this.state.geoData;
    if (!fc?.features) return;
  
    const feat = fc.features.find(f => {
      const props = f.properties || {};
      const candidates = [
        props.PCON24CD,
        props.WD24CD,
        props.LAD23CD,
        props.area_id,
        props.area_cd,
      ].filter(Boolean);
      return candidates.some((c) => String(c) === String(areaId));
    });
  
    if (!feat) return;

    const selected = this.state.selectedFeature;
    if (
      this.state.geoLevel === LEVEL_WARD &&
      selected?.level === LEVEL_WARD &&
      String(selected.value) === String(areaId) &&
      this.state.selectedConstituencyBounds
    ) {
      // Re-zooming to the already-selected ward steps back up to the area it
      // was drilled into from — a constituency or a local authority.
      const parentLevel = this.getParentLevel();
      const parentCode = this.state.selectedConstituencyCode || null;
      const parentLabel =
        this.state.selectedConstituencyName ||
        `${levelLabel(parentLevel)} ${parentCode || ""}`.trim();

      if (this.mapRef.current?.getMap) {
        this.mapRef.current.getMap().fitBounds(this.state.selectedConstituencyBounds, { padding: 28 });
      }

      const parentFeature = {
        level: parentLevel,
        key: levelIdKey(parentLevel),
        value: parentCode,
        name: parentLabel,
        rowMatchIds: parentCode ? [String(parentCode)] : [],
      };

      this.setState(
        { selectedFeature: parentFeature },
        () => {
          const data = this.getAggregatesForSidebar?.();
          this.props.onFeatureSelect?.({
            feature: parentFeature,
            data,
            constCode: parentCode,
            constName: parentLabel,
          });
        }
      );
      return;
    }

    const level = this.state.geoLevel;
    const bounds = this.getFeatureBounds(feat);
    const props = feat.properties || {};
    // At a top level the area zoomed to IS the parent to drill into; at ward
    // level the parent is the ward's own constituency.
    const parentCode = isTopLevel(level)
      ? props[levelIdKey(level)]
      : (parsePCON24CDs(props.PCON24CD)[0] ?? null);
    const parentName = isTopLevel(level) ? props[levelNameKey(level)] : props.PCON24NM;
    const featureName =
      props.NAME ||
      props[levelNameKey(level)] ||
      props.PCON24NM ||
      props.WD24NM ||
      props.LAD23NM ||
      String(areaId);
    if (bounds && this.mapRef.current?.getMap) {
      this.mapRef.current.getMap().fitBounds(bounds, { padding: 24 });
    }

    if (isTopLevel(level) && parentCode) {
      const parentLabel = parentName || featureName;
      const parentFeature = {
        level,
        key: levelIdKey(level),
        value: String(parentCode),
        name: parentLabel,
        rowMatchIds: [String(parentCode)],
      };

      this.setState(
        {
          selectedConstituencyCode: parentCode,
          selectedConstituencyName: parentLabel,
          selectedConstituencyBounds: bounds || this.state.selectedConstituencyBounds,
          selectedParentLevel: level,
          geoLevel: LEVEL_WARD,
          selectedFeature: parentFeature,
        },
        async () => {
          await this.fetchMetrics(level, parentCode);
          if (typeof this.fetchSecondaryMetrics === "function") {
            await this.fetchSecondaryMetrics(level, parentCode);
          }
          await this.loadGeo(LEVEL_WARD, parentCode, { doFit: false });
          await this.fetchMetrics(LEVEL_WARD, parentCode);
          if (typeof this.fetchSecondaryMetrics === "function") {
            await this.fetchSecondaryMetrics(LEVEL_WARD, parentCode);
          }
          const data = this.getAggregatesForSidebar?.();
          this.props.onFeatureSelect?.({
            feature: parentFeature,
            data,
            constCode: parentCode,
            constName: parentLabel,
          });
        }
      );
      return;
    }

    this.setState({
      selectedFeature: {
        level,
        key: levelIdKey(level),
        value: String(areaId),
        name: featureName,
        rowMatchIds: [String(areaId)],
      },
      selectedConstituencyCode: parentCode || this.state.selectedConstituencyCode,
      selectedConstituencyName: parentName || this.state.selectedConstituencyName,
      selectedConstituencyBounds: bounds || this.state.selectedConstituencyBounds,
    });
  };

  /** Computes [minLng,minLat] x [maxLng,maxLat] bounds for a feature */
  getFeatureBounds = (feature) => getFeatureBounds(feature);

  /**
   * Fetch national constituency baseline for sidebar aggregates
   * (one row per constituency for the active group/filters).
   */
  fetchConstituenciesBaseline = async () => {
    this.setState({ baselineLoading: true });
    await fetchConstituenciesBaseline({
      getActiveGroup: this.getActiveGroup,
      getFilters:     this.getFilters,
      props:          this.props,
      setState:       (u) => this.setState(u),
      _tracking:      this._baselineTracking,
    });
    this.setState({ baselineLoading: false });
  };
  
  
  /** Resolve constituency name for current selectedConstituencyCode */
  getSelectedConstituencyName = () => {
    const code = this.state.selectedConstituencyCode;
    if (!code) return null;
    const feature = this.state.geoData?.features?.find(
      (f) => f.properties?.PCON24CD === code
    );
    return feature?.properties?.PCON24NM || `Constituency ${code}`;
  };

  
  /** Load geometry for the given level and optional constituency */
  loadGeo = async (level, constituencyCode = null, options = {}) => {
    this.setState((prev) => ({ geoLoading: { ...prev.geoLoading, [level]: true } }));
    try {
      return await loadGeo({
        level,
        constituencyCode,
        doFit: options.doFit ?? true,
        bounds: options.bounds ?? null,
        backgroundOnly: options.backgroundOnly ?? false,
        updateAreas: options.updateAreas ?? true,
        setState: (updater, cb) => this.setState(updater, cb),
        mapRef: this.mapRef,
        fitToGeo: this.fitToGeo,
        normalizeId,
        handlePossibleAreasChange: this.props.handlePossibleAreasChange,
      });
    } finally {
      this.setState((prev) => ({ geoLoading: { ...prev.geoLoading, [level]: false } }));
    }
  };

callAllMetrics = (lvl, codeOrNull) => this.fetchMetrics(lvl, codeOrNull);


  /** Delegate click handling to utility, wiring state + callbacks */
  onMapClick = (evt) => {
    handleMapClick({
      evt,
      state: this.state,
      mapRef: this.mapRef,
      setState: (updater, cb) => this.setState(updater, cb),
      fetchMetrics: this.fetchMetrics,
      fetchSecondaryMetrics: this.fetchSecondaryMetrics,
      loadGeo: this.loadGeo,
      getAggregatesForSidebar: this.getAggregatesForSidebar,
      onFeatureSelect: this.props.onFeatureSelect,
    });
  };



  /** Handles switching between constituency/ward levels */
  handleGeoLevelChange = (newLevel) => {
  handleGeoLevelChange({
    newLevel,
    state: this.state,
    mapRef: this.mapRef,
    setState: (updater, cb) => this.setState(updater, cb),
    fitBoundsFromFeatures: this.fitBoundsFromFeatures,
    fetchMetrics: this.fetchMetrics,
    fetchSecondaryMetrics: this.fetchSecondaryMetrics,
    loadGeo: this.loadGeo,
    getAggregatesForSidebar: this.getAggregatesForSidebar,
    onFeatureSelect: this.props.onFeatureSelect,
  });
};

  /** Fit bounds to a FeatureCollection */
  fitToGeo = (geo) => fitToGeoUtil(geo, this.mapRef);



  
  /** Fetch primary metrics for the given level/area */
  fetchMetrics = async (level, constituencyCode) => {
  await fetchMetrics({
    level,
    constituencyCode,
    props: this.props,
    setState: (updater) => this.setState(updater),
    getActiveGroup: this.getActiveGroup,
    getFilters: this.getFilters,
    requestKey: this.requestKey,
    getAggregatesForSidebar: this.getAggregatesForSidebar,
    _internals: {
      inflight: this._inflight,
      lastKey: this._lastKey,
      lastAt: this._lastAt,
    },
    tableFromIPC,
    rowsFromTable,
  });
  const filters = this.getFilters();
  const hasFilters = filters && Object.values(filters).some((v) => v != null);
  if (hasFilters) {
    // Always fetch fresh unfiltered data. Don't await — let it update sidebar
    // asynchronously. We do NOT clear metricRowsUnfiltered here so that if the
    // 30s cache returns immediately, the sidebar gets it in the same tick as
    // metricRows rather than blinking through a null state.
    this.fetchMetricsUnfiltered(level, constituencyCode);
    if (level === "ward" && constituencyCode) {
      this.fetchConstituencyUnfiltered(constituencyCode);
    } else {
      this.setState({ metricRowsConstituencyUnfiltered: null });
    }
  } else {
    this.setState({ metricRowsUnfiltered: null, metricRowsConstituencyUnfiltered: null });
  }
};

  fetchMetricsUnfiltered = async (level, constituencyCode) => {
    return fetchMetricsUnfilteredUtil({
      level,
      constituencyCode,
      getActiveGroup: this.getActiveGroup,
      props: this.props,
      setState: (u) => this.setState(u),
      _tracking: this._unfilteredTracking,
    });
  };

  /** The top level the current ward view was drilled into from. */
  getParentLevel = () => this.state.selectedParentLevel || LEVEL_CONSTITUENCY;

  /** Fetch parent-area unfiltered data (used when geoLevel=ward to get correct
   *  column keys for the "Everyone in <parent area>" comparison bar). */
  fetchConstituencyUnfiltered = async (constituencyCode) => {
    return fetchMetricsUnfilteredUtil({
      level: this.getParentLevel(),
      constituencyCode,
      getActiveGroup: this.getActiveGroup,
      props: this.props,
      setState: (u) => {
        const mapped = {};
        if ("metricRowsUnfiltered" in u) mapped.metricRowsConstituencyUnfiltered = u.metricRowsUnfiltered;
        if (Object.keys(mapped).length) this.setState(mapped);
      },
      _tracking: this._constituencyUnfilteredTracking,
    });
  };

  /** Fetch filtered parent-area data for a single area (fast).
   *  Updates metricRowsConstituency only — does NOT overwrite metricRows so
   *  the ward-level map display is unaffected. */
  fetchConstituencyFiltered = async (constituencyCode) => {
    const stateRemapper = (updater) => {
      const updates = typeof updater === "function" ? updater(this.state) : updater;
      // Drop metricRows (would overwrite ward data) and loading flag changes.
      // metricRowsConstituency is already set by fetchMetrics at a top level.
      const { metricRows, loading, ...rest } = updates;
      if (Object.keys(rest).length) this.setState(rest);
    };
    await fetchMetrics({
      level: this.getParentLevel(),
      constituencyCode,
      props: this.props,
      setState: stateRemapper,
      getActiveGroup: this.getActiveGroup,
      getFilters: this.getFilters,
      requestKey: this.requestKey,
      getAggregatesForSidebar: this.getAggregatesForSidebar,
      _internals: this._constituencyFilteredInternals,
      tableFromIPC,
      rowsFromTable,
    });
  };

  /** Fetch secondary/political metrics for the given level/area */
  fetchSecondaryMetrics = async (level, areaCode) => {
  await fetchSecondaryMetrics({
    level,
    areaCode,
    props: this.props,
    setState: (updater) => this.setState(updater),
    getActiveGroup: this.getActiveGroup,
    tableFromIPC,
    rowsFromTable,
  });
};
  
  /**
   * Handle selection changes originating from the toolbar autocomplete.
   * Keeps selected feature in sync and triggers appropriate fetches.
   */
  handleselectedFeatureChange = ({ level, value, name }) => {
    const { styledGeo, geoLevel } = this.state;
    if (!styledGeo?.features) return;
  
    const key = levelIdKey(level);
  
    // Find the matching feature
    const feature = styledGeo.features.find(
      f => String(f.properties?.[key]) === String(value)
    );
    if (!feature) return;
  
    // ✅ CAPTURE THE BOUNDS HERE
    const newBounds = this.fitBoundsFromFeatures([feature]);
  
    const props = feature.properties || {};
    const areaId = String(props[key]);
    const label = props[levelNameKey(level)] || props.PCON24NM || props.WD24NM || name || `Area ${areaId}`;
    const rowMatchIds = [areaId];
  
    this.setState(
      {
        selectedFeature: {
          level,
          key,
          value: areaId,
          name: label,
          rowMatchIds,
        },
        // A top-level area becomes the parent to drill into; the toolbar then
        // switches the map to ward geometry filtered to it.
        ...(isTopLevel(level)
          ? {
              selectedConstituencyCode: areaId,
              selectedConstituencyName: label,
              selectedConstituencyBounds: newBounds,
              selectedParentLevel: level,
            }
          : {}
        ),
        // If a ward was selected, keep parent context
        ...(level === LEVEL_WARD && props.PCON24CD
          ? { selectedConstituencyCode: parsePCON24CDs(props.PCON24CD)[0] ?? null }
          : {}),
      },
      () => {
        // ... rest of your existing callback logic ...
        const data = this.getAggregatesForSidebar?.();
        const selectedAreaLabel = this.state.selectedFeature?.name || null;
  
        this.props.onFeatureSelect?.({
          feature: this.state.selectedFeature,
          data,
          selectedAreaLabel,
          selectedFeatureLevel: level,
        });

        if (level === LEVEL_WARD) {
          this.fetchMetrics(LEVEL_WARD, this.state.selectedConstituencyCode);
        } else {
          this.fetchMetrics(level, areaId);
        }
      }
    );
  };
  
  onStyleLoad = () => {
    const map = this.mapRef.current?.getMap?.();
    if (map) {
      registerArrowImage(map);
      map.on("styleimagemissing", (e) => {
        if (e?.id === "compare-arrow") registerArrowImage(map);
      });
    }
    this.setState({ styleLoaded: true }, () => {
      this.fitUK();
    });
  };

  buildArrows = () => {
    const { geoData, metricRows } = this.state;
    const { compareWaves, waveOptions } = this.props;
    const activeGroup = this.getActiveGroup();
    const isTargetMode = this.props.ActionMode === "filter";
    const group = isTargetMode ? activeGroup?.TargetVariable1 : activeGroup;

    if (!compareWaves || !metricRows?.length || !geoData?.features?.length || !group) {
      this.setState({ compareArrows: null, compareRowsLatest: null, compareMetricRows: null, compareWavePair: null, compareStyledGeo: null, comparableKeys: null, newThisWaveKeys: null });
      return;
    }

    const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    const sentimentTokenKind = (token) => {
      const t = String(token || "").trim().toLowerCase();
      if (!t) return null;
      if (["trust", "comfort", "positive", "pos", "support"].includes(t)) return "pos";
      if (["distrust", "discomfort", "negative", "neg", "oppose"].includes(t)) return "neg";
      if (t === "net") return "net";
      return null;
    };
    const targetSubRaw = String(this.props.ActionTargetSubVariables?.TargetSubVariable1 || "").trim();
    let valueKey = this.props.compareKey || "__top__";
    if (isTargetMode) {
      // In Find Target Areas compare mode, always compare the selected response.
      valueKey = targetSubRaw || group?.keys?.[0] || valueKey;
    }

    const sorted = [...(waveOptions || [])].sort((a, b) => b - a);
    if (sorted.length < 2) {
      this.setState({ compareArrows: null, compareRowsLatest: null, compareMetricRows: null, compareWavePair: null, newThisWaveKeys: null });
      return;
    }
    const [latestWave, prevWave] = sorted;
    const compareWavePair = [latestWave, prevWave];

    const latestRows = metricRows.filter((r) => Number(r.Wave) === Number(latestWave));
    const prevRows   = metricRows.filter((r) => Number(r.Wave) === Number(prevWave));
    const prevById   = new Map(prevRows.map((r) => {
      const id = String(r.area_cd ?? r.PCON24CD ?? r.WD24CD ?? r.area_id ?? "");
      return [id, r];
    }));

    // Latest wave rows with area_id normalised — used by tooltip for absolute values
    const compareRowsLatest = latestRows.map((r) => ({
      ...r,
      area_id: String(r.area_cd ?? r.PCON24CD ?? r.WD24CD ?? r.area_id ?? ""),
    }));

    const isVoteIntention = String(group?.label || "").toLowerCase().includes("vote intention");
    const isDontKnowOption = (k) => {
      const kk = String(k || "").toLowerCase();
      const lbl = String(group?.labels?.[k] || "").toLowerCase();
      return kk.includes("dontknow") || lbl.includes("don't know") || lbl.includes("dont know");
    };
    const movementKeys = (group?.keys || [valueKey]).filter((k) => !(isVoteIntention && isDontKnowOption(k)));
    if (isVoteIntention && isDontKnowOption(valueKey)) valueKey = "__top__";

    // Delta rows for ALL keys — used by tooltip for ↑/↓ badges.
    const allKeys = movementKeys.length ? movementKeys : (group?.keys || [valueKey]);

    // Options with a real previous-wave value somewhere: a like-for-like
    // comparison. A flat 0 across every area doesn't count — the column spans
    // all waves even when the option wasn't on the ballot yet, so 0 everywhere
    // means "not asked", not "nobody picked it".
    const prevKeysPresent = new Set();
    for (const r of prevRows) {
      for (const k of allKeys) {
        const pv = Number(r[k]);
        if (r[k] != null && Number.isFinite(pv) && pv !== 0) prevKeysPresent.add(k);
      }
    }

    // Options that only exist from this wave — Restore on vote intention, Andy
    // Burnham and the other new names on best PM. There's nothing to subtract,
    // so the whole of their current share is the movement and they belong in
    // "biggest gainer". It isn't like-for-like growth though, so they're passed
    // to the tooltip to be labelled as new rather than shown as a plain rise.
    const newThisWaveKeys = new Set();
    for (const k of allKeys) {
      if (prevKeysPresent.has(k)) continue;
      const presentNow = latestRows.some((r) => {
        const lv = Number(r[k]);
        return r[k] != null && Number.isFinite(lv) && lv !== 0;
      });
      if (presentNow) newThisWaveKeys.add(k);
    }

    const isComparable = (k) => prevKeysPresent.has(k) || newThisWaveKeys.has(k);
    const comparableKeys = allKeys.filter(isComparable);
    // Also filter movementKeys so map colouring ignores keys with no data at all
    const comparableMovementKeys = movementKeys.filter(isComparable);
    const compareMetricRows = latestRows.map((latest) => {
      const id   = String(latest.area_cd ?? latest.PCON24CD ?? latest.WD24CD ?? latest.area_id ?? "");
      const prev = prevById.get(id);
      const deltaRow = { area_id: id };
      for (const k of comparableKeys) {
        const lv = Number(latest[k]);
        const pv = Number(prev?.[k]);
        deltaRow[k] = (Number.isFinite(lv) ? lv : 0) - (Number.isFinite(pv) ? pv : 0);
      }
      return deltaRow;
    });

    const baseGeo = this.state.styledGeo || geoData;
    const toHex = (hex) => String(hex || "").trim();
    const hexToRgb = (hex) => {
      const h = toHex(hex).replace("#", "");
      if (h.length !== 6) return null;
      const n = parseInt(h, 16);
      if (!Number.isFinite(n)) return null;
      return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
    };
    const mixWithWhite = (hex, t) => {
      const rgb = hexToRgb(hex);
      if (!rgb) return "#dbeafe";
      const k = Math.max(0, Math.min(1, t));
      const mix = (c) => Math.round(255 - (255 - c) * k);
      return `rgb(${mix(rgb.r)}, ${mix(rgb.g)}, ${mix(rgb.b)})`;
    };
    const areaIdFromFeature = (f) =>
      String(f?.properties?.area_id ?? f?.properties?.PCON24CD ?? f?.properties?.WD24CD ?? "");

    const isTopMover = !isTargetMode && valueKey === "__top__";
    const colourForSubVar = (k) =>
      (group?.type === "likert_grid" ? (group?.subColors?.[k] || "#2563eb") : (group?.colors?.[k] || "#2563eb"));
    const labelForSubVar = (k) =>
      (group?.type === "likert_grid" ? (group?.subLabels?.[k] || k) : (group?.labels?.[k] || k));

    // For "net", compute value from polarity pos/neg key deltas
    let arrowValueKey = valueKey;
    let arrowValueFn  = null;
    let posColor      = group?.colors?.[valueKey] || "#15803d";
    let negColor      = "#9ca3af";
    let selectedMovementLabel = null;

    if (isTargetMode && group?.type === "likert_grid") {
      const [selectedSubVarIdRaw, selectedMetricRaw] = String(valueKey || "").split("|");
      const selectedSubVarId = selectedSubVarIdRaw || group?.subVariables?.[0] || null;
      const selectedSubGroup = selectedSubVarId ? this.getGroups()?.[selectedSubVarId] : null;
      const metricKind = sentimentTokenKind(selectedMetricRaw);

      if (selectedSubGroup) {
        const posKeys = toArr(selectedSubGroup?.polarity?.pos);
        const negKeys = toArr(selectedSubGroup?.polarity?.neg);
        const netFn = (row) => {
          const pos = posKeys.reduce((a, k) => a + (Number(row?.[k]) || 0), 0);
          const neg = negKeys.reduce((a, k) => a + (Number(row?.[k]) || 0), 0);
          return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : NaN;
        };
        const pickFn = (keys) => (row) => keys.reduce((a, k) => a + (Number(row?.[k]) || 0), 0);

        if (metricKind === "pos") {
          arrowValueFn = pickFn(posKeys);
          arrowValueKey = null;
          selectedMovementLabel = `${group?.subLabels?.[selectedSubVarId] || selectedSubVarId} trust movement`;
        } else if (metricKind === "neg") {
          arrowValueFn = pickFn(negKeys);
          arrowValueKey = null;
          selectedMovementLabel = `${group?.subLabels?.[selectedSubVarId] || selectedSubVarId} distrust movement`;
        } else if (metricKind === "net") {
          arrowValueFn = netFn;
          arrowValueKey = null;
          selectedMovementLabel = `${group?.subLabels?.[selectedSubVarId] || selectedSubVarId} net movement`;
        } else if (selectedMetricRaw && selectedSubGroup?.keys?.includes(selectedMetricRaw)) {
          arrowValueKey = selectedMetricRaw;
          selectedMovementLabel = `${group?.subLabels?.[selectedSubVarId] || selectedSubVarId} movement`;
        } else {
          // Default to positive sentiment in target mode.
          arrowValueFn = pickFn(posKeys);
          arrowValueKey = null;
          selectedMovementLabel = `${group?.subLabels?.[selectedSubVarId] || selectedSubVarId} trust movement`;
        }

        posColor = group.subColors?.[selectedSubVarId] || "#15803d";
        negColor = "#b91c1c";
        // Keep labels/colors aligned with the selected sub-variable in target mode.
        valueKey = selectedSubVarId;
      }
    }

    if (valueKey === "net" && group?.polarity) {
      const posKeys = toArr(group.polarity.pos);
      const negKeys = toArr(group.polarity.neg);
      arrowValueFn  = (row) => {
        const pos = posKeys.reduce((a, k) => a + (Number(row?.[k]) || 0), 0);
        const neg = negKeys.reduce((a, k) => a + (Number(row?.[k]) || 0), 0);
        return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : NaN;
      };
      arrowValueKey = null;
      posColor      = "#15803d";
      negColor      = "#b91c1c";
    } else if (group?.type === "likert_grid" && valueKey) {
      // compareKey is a sub-variable ID (e.g. "trust_banks") — compute its net delta
      const subGroup = this.getGroups()[valueKey];
      if (subGroup?.polarity) {
        const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
        const posKeys = toArr(subGroup.polarity.pos);
        const negKeys = toArr(subGroup.polarity.neg);
        arrowValueFn  = (row) => {
          const pos = posKeys.reduce((a, k) => a + (Number(row?.[k]) || 0), 0);
          const neg = negKeys.reduce((a, k) => a + (Number(row?.[k]) || 0), 0);
          return Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : NaN;
        };
        arrowValueKey = null;
        posColor      = group.subColors?.[valueKey] || "#15803d";
        negColor      = "#b91c1c";
      }
    }

    // Compare choropleth:
    //  - "__top__": color each area by the party/sector with biggest positive movement.
    //  - "__top_neg__": color each area by the party/sector with biggest negative movement.
    //  - specific key: color by that key's movement strength in that key's color.
    let compareStyledGeo = null;
    let compareArrows = null;

    if (isTopMover || valueKey === "__top_neg__") {
      const isTopNegativeMover = valueKey === "__top_neg__";
      const areaTop = new Map();
      let maxMag = 0;

      if (group?.type === "likert_grid") {
        const subVars = group?.subVariables || [];
        latestRows.forEach((latest) => {
          const id = String(latest.area_cd ?? latest.PCON24CD ?? latest.WD24CD ?? latest.area_id ?? "");
          const prev = prevById.get(id);
          let best = null;
          subVars.forEach((sv) => {
            const subGroup = this.getGroups()[sv];
            const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
            const posKeys = toArr(subGroup?.polarity?.pos);
            const negKeys = toArr(subGroup?.polarity?.neg);
            if (!posKeys.length && !negKeys.length) return;
            const nowPos = posKeys.reduce((a, k) => a + (Number(latest?.[k]) || 0), 0);
            const nowNeg = negKeys.reduce((a, k) => a + (Number(latest?.[k]) || 0), 0);
            const prevPos = posKeys.reduce((a, k) => a + (Number(prev?.[k]) || 0), 0);
            const prevNeg = negKeys.reduce((a, k) => a + (Number(prev?.[k]) || 0), 0);
            const d = (nowPos - nowNeg) - (prevPos - prevNeg);
            if (!Number.isFinite(d)) return;
            if (isTopNegativeMover) {
              if (d >= 0) return;
              if (!best || d < best.delta) best = { key: sv, delta: d };
            } else {
              if (d <= 0) return;
              if (!best || d > best.delta) best = { key: sv, delta: d };
            }
          });
          if (best) {
            areaTop.set(id, best);
            maxMag = Math.max(maxMag, Math.abs(best.delta));
          }
        });
      } else {
        compareMetricRows.forEach((row) => {
          const id = String(row.area_id ?? "");
          let best = null;
          comparableMovementKeys.forEach((k) => {
            const d = Number(row?.[k]);
            if (!Number.isFinite(d)) return;
            if (isTopNegativeMover) {
              if (d >= 0) return;
              if (!best || d < best.delta) best = { key: k, delta: d };
            } else {
              if (d <= 0) return;
              if (!best || d > best.delta) best = { key: k, delta: d };
            }
          });
          if (best) {
            areaTop.set(id, best);
            maxMag = Math.max(maxMag, Math.abs(best.delta));
          }
        });
      }

      const safeMax = maxMag || 1;
      compareStyledGeo = {
        type: "FeatureCollection",
        features: (baseGeo?.features || []).map((f) => {
          const id = areaIdFromFeature(f);
          const rec = areaTop.get(id);
          if (!rec) return { ...f, properties: { ...f.properties, fillColor: "#d1d5db" } };
          const t = 0.2 + 0.8 * Math.sqrt(Math.abs(rec.delta) / safeMax);
          const base = colourForSubVar(rec.key);
          return {
            ...f,
            properties: {
              ...f.properties,
              fillColor: mixWithWhite(base, t),
              topLabel: `${labelForSubVar(rec.key)} ${isTopNegativeMover ? "negative" : "positive"} movement`,
              topValue: rec.delta,
            },
          };
        }),
      };
      // In top-mover mode, keep map clean: party-by-area fills without arrows.
      compareArrows = null;
    } else {
      const rowValue = (row) => {
        if (!row) return NaN;
        if (arrowValueFn) return Number(arrowValueFn(row));
        return Number(row?.[arrowValueKey]);
      };
      const valuesById = new Map();
      const allDeltaValues = [];
      compareMetricRows.forEach((row) => {
        const id = String(row.area_id ?? "");
        const v = rowValue(row);
        if (!Number.isFinite(v)) return;
        valuesById.set(id, v);
        allDeltaValues.push(v);
      });

      // Diverging green→red choropleth: green = gaining, red = losing.
      // This makes direction immediately obvious regardless of party color.
      const divergeScale = make2pointHeatScale("#15803d", "#b91c1c", allDeltaValues);

      compareStyledGeo = {
        type: "FeatureCollection",
        features: (baseGeo?.features || []).map((f) => {
          const id = areaIdFromFeature(f);
          const v = valuesById.get(id);
          if (!Number.isFinite(v)) return { ...f, properties: { ...f.properties, fillColor: "#e5e7eb" } };
          return {
            ...f,
            properties: {
              ...f.properties,
              fillColor: divergeScale(v),
              topLabel: selectedMovementLabel || `${labelForSubVar(valueKey)} movement`,
              topValue: v,
            },
          };
        }),
      };

      // Arrows: green for gaining, red for losing — clear universal signal.
      compareArrows = this.state.centroids?.size
        ? buildCompareArrows({
            centroidMap: this.state.centroids,
            deltaRows: compareMetricRows,
            valueKey: arrowValueKey,
            valueFn: arrowValueFn,
            posColor: "#16a34a",
            negColor: "#dc2626",
          })
        : null;
    }

    if (isTargetMode && compareStyledGeo?.features?.length) {
      const rankedMovement = compareStyledGeo.features
        .map((f) => {
          const id = areaIdFromFeature(f);
          const props = f?.properties || {};
          const delta = Number(props.topValue);
          if (!id || !Number.isFinite(delta)) return null;
          const abs = Math.abs(delta);
          return {
            id,
            name: props.WD24NM || props.PCON24NM || props.NAME || id,
            // Keep TopAreasPanel scale as 0..1 while tolerating both ratio and percent-point deltas.
            score: abs <= 1 ? abs : abs / 100,
            delta,
          };
        })
        .filter(Boolean)
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
      this.props.onRankedUpdate?.(rankedMovement);
    }

    this.setState({ compareArrows, compareRowsLatest, compareMetricRows, compareWavePair, compareStyledGeo, comparableKeys: new Set(comparableKeys), newThisWaveKeys });
  };
  
  /** Rebuild styledGeo from geoData + metricRows */
  restyle = () => {
   const { geoData, metricRows } = this.state;
  const group = this.getActiveGroup();

  restyleGeo({
    geoData,
    metricRows,
    group,
    OVERLAP_MODE:OVERLAP_MODE,
    props: this.props,
    setStyledGeo: (styledGeo) => this.setState({ styledGeo }),
    getGroups: this.getGroups,
    buildLabelsAndColors,
    makeHeatScale,
    make2pointHeatScale,
    onRankedUpdate:this.props.onRankedUpdate,
    waveSelect:this.props.waveSelect
  });
};

  
  
  onClearSelection = () => {
  this.setState({ selectedFeature: null });
};

  /** Drill back up one level (ward -> its parent area -> national) */
  goBackOneLevel = () => {
  const { geoLevel, selectedConstituencyCode, selectedConstituencyBounds } = this.state;
  const map = this.mapRef.current?.getMap();

  let next;

  if (geoLevel === LEVEL_WARD) {
    next = this.getParentLevel();
  } else if (isTopLevel(geoLevel)) {
    // Nothing above a top level — go all the way back to the national
    // constituency view.
    this.resetView();
    return;
  } else {
    next = LEVEL_CONSTITUENCY;
  }

  this.handleGeoLevelChange(next);

  if (geoLevel === LEVEL_WARD) {
    if (selectedConstituencyCode) {
      // The parent's name is already in state from the drill-in. Looking it up
      // in geoData would not work here anyway: at ward level geoData holds ward
      // features, which carry their parent's code but not its name.
      const parentLabel =
        this.state.selectedConstituencyName ||
        `${levelLabel(next)} ${selectedConstituencyCode}`;

      this.setState({
        selectedFeature: {
          level: next,
          key: levelIdKey(next),
          value: selectedConstituencyCode,
          name: parentLabel,
        },
      });

      const data = this.getAggregatesForSidebar();
      this.props.onFeatureSelect?.({
        feature: this.state.selectedFeature,
        data,
        selectedConstituencyName: parentLabel,
      });

      if (selectedConstituencyBounds) {
        map?.fitBounds(selectedConstituencyBounds, { padding: 28, duration: 500 });
      }
    } else {
      this.setState({ selectedFeature: null });
    }
  }

  if (geoLevel === LEVEL_CONSTITUENCY) {
    this.setState({ selectedFeature: null });

    this.props.onFeatureSelect?.({
      feature: null,
      data: null,
      selectedConstituencyName: null,
    });

    map?.fitBounds(MAINLAND_BOUNDS, { padding: 24, duration: 600 });
  }
};


  
  /** Build { national, constituency, selected } aggregates for sidebar */
  getAggregatesForSidebar = (overrides = {}) => aggregatesForSidebar({
    selectedFeature:          this.state.selectedFeature,
    selectedConstituencyCode: this.state.selectedConstituencyCode,
    metricRows:               this.state.metricRows,
    metricRowsConstituency:   this.state.metricRowsConstituency,
    metricRowsConstituencies: this.state.metricRowsConstituencies,
    metricRowsUnfiltered:     "metricRowsUnfiltered" in overrides ? overrides.metricRowsUnfiltered : this.state.metricRowsUnfiltered,
    metricRowsConstituencyUnfiltered: this.state.metricRowsConstituencyUnfiltered,
    group:        this.getActiveGroup(),
    groups:       this.getGroups(),
    themeColours: this.props.theme_colours,
    waveSelect:        this.props.waveSelect,
    preferenceFilter:  this.props.preferenceFilter,
  });


  /** Reset to initial nationwide constituency view */
  resetView = () => {
  // Clear chat highlights
  this._applyChatFeatureState(this.props.chatHighlightAreas, []);
  this.props.onClearChatHighlights?.();

  this.setState(
    {
      geoLevel: "constituency",
      selectedConstituencyCode: null,
      selectedConstituencyName: null,
      selectedConstituencyBounds: null,
      selectedParentLevel: null,
      selectedFeature: null,
      metricRows: null,
      styledGeo: null,
    },
    () => {
      this.loadGeo("constituency", null, { doFit: true, bounds: MAINLAND_BOUNDS });
      this.fetchMetrics("constituency", null);


      const data = this.getAggregatesForSidebar();
      this.props.onFeatureSelect?.({
        feature: null,
        data,
        constName: null,
      });

      const map = this.mapRef.current?.getMap();
      map?.fitBounds(MAINLAND_BOUNDS, { padding: 24, duration: 600 });
    }
  );
};

  /** Fit and return bounds covering an array of features */
  fitBoundsFromFeatures = (features) => fitBoundsUtil(features, this.mapRef);

  /** Update Mapbox feature-state for chat highlights (clears old, sets new) */
  _applyChatFeatureState = (prevAreas, nextAreas) => {
    const map = this.mapRef.current?.getMap?.();
    if (!map) return;
    const sourceId = "geo";
    // Clear previous
    (prevAreas || []).forEach((code) => {
      try { map.setFeatureState({ source: sourceId, id: code }, { chatHighlight: false }); } catch (_) {}
    });
    // Set new
    (nextAreas || []).forEach((code) => {
      try { map.setFeatureState({ source: sourceId, id: code }, { chatHighlight: true }); } catch (_) {}
    });
  };

  /** Zoom to the subset of geoData matching the chat highlight area codes */
  zoomToChatAreas = (areaCodes) => {
    if (!areaCodes?.length || !this.state.geoData?.features) return;
    const codeSet = new Set(areaCodes);
    const features = this.state.geoData.features.filter(
      (f) => codeSet.has(f.properties?.PCON24CD) || codeSet.has(f.properties?.WD24CD)
    );
    if (features.length > 0) {
      this.fitBoundsFromFeatures(features);
    }
  };

  /** Track hover for popup + outline */
  onMouseMove = (evt) => {
  const feature = evt.features && evt.features[0];
  if (!feature) {
    this.setState({ hoverInfo: null, hoverAreaId: null });
    this.props.handlehoverAreaChange(null)
    return;
  }

  const { lng, lat } = evt.lngLat;
  const { x, y } = evt.point || {};

  const props = feature.properties || {};
  const areaId = props.area_cd ?? props.PCON24CD ?? props.WD24CD ?? null;

  this.props.handlehoverAreaChange(areaId)

  this.setState({
    hoverInfo: { lon: lng, lat, x, y, props },
    hoverAreaId: areaId,
  });
};



render() {
    const active_group = this.getActiveGroup()

  const { geoLevel, styledGeo, geoData, hoverInfo, metricRows } = this.state;
  const displayGeo = (this.props.compareWaves && this.state.compareStyledGeo) ? this.state.compareStyledGeo : (styledGeo || geoData);

  const highlightCodes = this.props.chatHighlightAreas || [];
  const hasChatHighlight = highlightCodes.length > 0;
  // Evaluate highlights directly from feature properties so they survive source/style updates
  // and can still highlight whole constituencies while viewing ward geometries.
  const chatMatchExpr = geoLevel === LEVEL_WARD
    ? [
        "any",
        ["in", ["get", "WD24CD"], ["literal", highlightCodes]],
        ["in", ["get", "PCON24CD"], ["literal", highlightCodes]],
      ]
    : ["in", ["get", levelIdKey(geoLevel)], ["literal", highlightCodes]];
  const chatDimOpacity = ["case", chatMatchExpr, 0, hasChatHighlight ? 0.62 : 0];
  const chatFillOpacity = ["case", chatMatchExpr, hasChatHighlight ? 0.08 : 0, 0];
  const chatBorderFilter = chatMatchExpr;

  const disableInteraction = !!this.props.disableInteraction;

  return (
<div style={{ height: "100%", width: "100%" }}>


      <ReactMap
        ref={this.mapRef}
        initialViewState={{ longitude: -2.5, latitude: 54.5, zoom: 8.2 }}
        style={{ width: "100%", height: "100%" }}
        projection={{ name: "mercator" }}
        mapStyle={MAP_STYLE}
        mapboxAccessToken={MAPBOX_TOKEN}
        interactiveLayerIds={["geo-fill"]}
        renderWorldCopies={false}
        onClick={disableInteraction ? undefined : this.onMapClick}
        onMouseMove={this.onMouseMove}
        onLoad={this.onStyleLoad}
        dragPan={!disableInteraction}
        scrollZoom={!disableInteraction}
        dragRotate={!disableInteraction}
        doubleClickZoom={!disableInteraction}
        touchZoomRotate={!disableInteraction}
      >
        {this.state.styleLoaded &&
          displayGeo &&
          displayGeo?.features && (
            <Source id="geo" type="geojson" data={displayGeo} tolerance={0.6} promoteId={levelIdKey(geoLevel)}>
            <Layer
              id="geo-fill"
              type="fill"
              paint={{
                "fill-color": ["coalesce", ["get", "fillColor"], "#90caf9"], // <-- use blue fallback
                "fill-color-transition": { duration: 400, delay: 0 },
                "fill-opacity": 0.6,
                "fill-opacity-transition": { duration: 400, delay: 0 }
              }}
            />


{hoverInfo && (
  <Layer
    id="geo-hover"
    type="line"
    paint={{
      "line-color": "#ffffff",
      "line-width": 2,
    }}
    filter={[
      "==",
      ["get", levelIdKey(geoLevel)],
      hoverInfo?.props?.[levelIdKey(geoLevel)] ?? "",
    ]}
  />
)}

<Layer
  id="geo-selected-halo"
  type="line"
  paint={{
    "line-color": "#ffffff",
    "line-width": 4,
  }}
  filter={
    this.state.selectedFeature?.level === geoLevel &&
    this.state.selectedFeature?.value
      ? [
          "==",
          ["get", levelIdKey(geoLevel)],
          this.state.selectedFeature.value,
        ]
      : ["==", ["get", "__none__"], "__none__"]
  }
/>
          <Layer
            id="geo-outline"
            type="line"
            paint={{
              "line-color": "#DADADA",
              "line-width": 0.6,
              "line-opacity": 0.95,
            }}
          />

          {/* ── Chat highlight layers — always mounted, controlled via paint/filter ── */}
          {/* 1. Dim overlay on non-highlighted areas */}
          <Layer
            id="chat-dim"
            type="fill"
            paint={{
              "fill-color": "#f0f4ff",
              "fill-opacity": chatDimOpacity,
              "fill-opacity-transition": { duration: 350, delay: 0 },
            }}
          />
          {/* 2. Subtle blue tint on highlighted areas */}
          <Layer
            id="chat-highlight-fill"
            type="fill"
            filter={chatBorderFilter}
            paint={{
              "fill-color": "#1d4ed8",
              "fill-opacity": chatFillOpacity,
              "fill-opacity-transition": { duration: 350, delay: 0 },
            }}
          />
          {/* 3. Soft glow border (wider, low opacity) */}
          <Layer
            id="chat-highlight-glow"
            type="line"
            filter={chatBorderFilter}
            paint={{
              "line-color": "#3b82f6",
              "line-width": 6,
              "line-opacity": 0.25,
            }}
          />
          {/* 4. Crisp inner border on highlighted areas */}
          <Layer
            id="chat-highlight-border"
            type="line"
            filter={chatBorderFilter}
            paint={{
              "line-color": "#1d4ed8",
              "line-width": 2,
              "line-opacity": 1,
            }}
          />

          </Source>
          
          )}

        {this.state.styleLoaded &&
          this.props.compareWaves &&
          this.props.showArrows !== false &&
          this.state.compareArrows?.features?.length > 0 && (
            <Source id="compare-arrows" type="geojson" data={this.state.compareArrows}>
              <Layer
                id="compare-arrows-layer"
                type="symbol"
                layout={{
                  "icon-image": "compare-arrow",
                  "icon-rotate": ["get", "angle"],
                  "icon-size": ["get", "size"],
                  "icon-allow-overlap": true,
                  "icon-ignore-placement": true,
                  "icon-rotation-alignment": "map",
                  "icon-anchor": "center",
                }}
                paint={{
                  "icon-color": ["get", "color"],
                  "icon-opacity": ["get", "opacity"],
                }}
              />
            </Source>
          )}

        {hoverInfo && !this.props.isSidebarHover && !(disableInteraction && hoverInfo.y < 55) && (
          <MapHoverPopup
            hoverInfo={hoverInfo}
            geoLevel={geoLevel}
            group={this.getEffectiveGroup()}
            groups={this.getGroups()}
            themeColours={this.props.theme_colours}
            actionMode={this.props.ActionMode}
            preferenceFilter={this.getEffectivePreferenceFilter()}
            actionTargetSubVariables={this.props.ActionTargetSubVariables}
            filters={this.getFilters()}
            metricRows={metricRows}
            metricRowsUnfiltered={this.state.metricRowsUnfiltered}
            metricRowsConstituencies={this.state.metricRowsConstituencies}
            selectedConstituencyCode={this.state.selectedConstituencyCode}
            selectedDemographics={this.props.selectedDemographics}
            mapRef={this.mapRef}
            normalizeAreaIdForLevel={this.normalizeAreaIdForLevel}
            getLatestTwoWaves={this.getLatestTwoWaves}
            compareWaves={this.props.compareWaves}
            compareWavePair={this.state.compareWavePair}
            compareRowsLatest={this.state.compareRowsLatest}
            compareMetricRows={this.state.compareMetricRows}
          newThisWaveKeys={this.state.newThisWaveKeys}
            waveSelect={this.props.waveSelect}
            compareKey={this.props.compareKey}
          />
        )}

        {!disableInteraction && <MapToolbar
          geoLevel={geoLevel}
          onGeoLevelChange={this.handleGeoLevelChange}
          selectedConstituencyName={this.state.selectedConstituencyName}
          selectedConstituencyCode={this.state.selectedConstituencyCode}
          selectedAreaLabel={this.state.selectedFeature?.name || null}
          onClearSelection={this.onClearSelection}
          zoomIn={() => this.mapRef.current?.getMap().zoomIn()}
          zoomOut={() => this.mapRef.current?.getMap().zoomOut()}
          resetView={this.resetView}
          mode={this.props.mode}
          onModeChange={(m) => this.props.onModeChange?.(m)}
          onResetDemographics={this.props.onClearAll}
          filters={this.props.filters || {}}
          onFiltersChange={(f) => this.props.onFiltersChange?.(f)}
          goBackOneLevel={this.goBackOneLevel}
          poliInfo={this.state.SecondarymetricRows}
          hoverArea={this.state.hoverAreaId}
          poliInfoToplevel={this.state.SecondarymetricRowstoplevel}
          selectedFeature={this.state.selectedFeature || null}
          selectedDemographics={this.props.selectedDemographics}
          handleselectedFeatureChange = {this.handleselectedFeatureChange}
          PossibleAreas={this.props.PossibleAreas}
          metricRows={this.state.metricRows}
          group={this.getActiveGroup()}
          callAllMetrics={this.callAllMetrics}
          loadGeo={this.loadGeo}
          geoLoading={this.state.geoLoading}

           waveSelect={this.props.waveSelect}
        handlewaveSelectChange={this.props.handlewaveSelectChange}
        active_group={this.props.ActionMode === "filter" ? active_group?.TargetVariable1 : active_group}
        waveOptions={this.props.waveOptions}
        compareWaves={this.props.compareWaves}
        onCompareWavesChange={this.props.onCompareWavesChange}
        showArrows={this.props.showArrows}
        onShowArrowsToggle={this.props.onShowArrowsToggle}
          compareKey={this.props.compareKey}
          onCompareKeyChange={this.props.onCompareKeyChange}
          actionMode={this.props.ActionMode}
          comparableKeys={this.state.comparableKeys}
        />}
      </ReactMap>
    </div>
  );
}
}

export default withRouter(MapPage);
